import FILTER_TYPES from '@/components/query/filter-types';

/**
 * @property {string} name
 * @property {string} type
 * @property {{}} definition
 */
export class ResolvedField {
    /**
     * Build a resolved field structure
     * @param {string} name
     * @param {string} type
     * @param {{}} definition
     */
    constructor({name, type, definition}) {
        this.name = name;
        this.type = type;
        this.definition = definition;
    }

    /**
     * Check whether the field has null for all properties.
     * @return {boolean}
     */
    get isBlank() {
        return Object.keys(this)
            .every(key => this[key] === null);
    }

    static getBlankField() {
        return new ResolvedField({
            name: null,
            type: null,
            definition: null,
        });
    }
}

export default class FieldProvider {
    static FieldResolveCache = {};

    static PathDelimiter = '/';

    /**
     * Initialize the provider.
     * @param {{}} pathways
     * @param {function} pathwayResolver
     * @param {{}} customFieldsStore
     * @param {function} typeResolver
     */
    constructor({
        pathways, pathwayResolver, customFieldsStore, typeResolver,
    }) {
        this.pathways = pathways;
        this.pathwayResolver = pathwayResolver;
        this.typeResolver = typeResolver;
        this.customFieldsStore = customFieldsStore;
        this.fieldGroups = {};
        this.resolvePathways();
    }

    /**
     * Generate the index of groups and fields for the provider.
     */
    resolvePathways() {
        this.fieldGroups = this.pathwayResolver.call(null, this.pathways, this.customFieldsStore);
    }

    /**
     * Resolve the field from the normalized condition
     * @param {NormalizedCondition} condition
     * @return {ResolvedField}
     */
    resolveFieldFromCondition(condition) {
        // special condition
        if (condition.isRandom) {
            return new ResolvedField({
                name: null,
                definition: null,
                type: FILTER_TYPES.random,
            });
        }
        return this.resolveFieldFromPath(condition.getPath());
    }

    /**
     * Resolve the field by using its path as an unique id
     * @param {string} id
     * @return {ResolvedField}
     */
    resolveFieldFromPath(id) {
        // use cache if present
        if (Reflect.has(FieldProvider.FieldResolveCache, id)) {
            return FieldProvider.FieldResolveCache[id];
        }
        const lineage = id.replace(/^\/|\/$/g, '')
            .split(FieldProvider.PathDelimiter);
        let group = null;
        // resolve lineage from start to second to last
        // to skip the actual field prop
        lineage.slice(0, -1)
            .forEach((prefix, index) => {
                if (group === null) {
                    const slice = lineage.slice(0, -(index + 1));
                    const path = `${FieldProvider.PathDelimiter}${slice.join(FieldProvider.PathDelimiter)}`;
                    if (Reflect.has(this.fieldGroups, path)) {
                        group = [path, slice.length];
                    }
                }
            });
        if (group !== null) {
            const field = ResolvedField.getBlankField();
            const [path, offset] = group;
            const steps = lineage.slice(offset);
            // resolve field definition
            const parts = [{
                step: path,
                def: this.fieldGroups[path],
            }];
            field.definition = steps.reduce((acc, val) => {
                if (acc && val && Reflect.has(acc, val)) {
                    parts.push({
                        step: `/${val}`,
                        def: acc[val],
                    });
                    acc = acc[val];
                    return acc;
                }
                return null;
            }, this.fieldGroups[path].fields);
            // no definition means the field doesn't exist in the spec whether by design
            // or because the spec resolver failed to expose it -- in either case we exit
            if (field.definition === null) {
                FieldProvider.FieldResolveCache[id] = ResolvedField.getBlankField();
                return FieldProvider.FieldResolveCache[id];
            }
            field.steps = parts;
            field.name = steps[steps.length - 1];
            field.type = this.typeResolver(field.definition);
            field.autoComplete = this.resolveAutoCompletionSupport({id, path, field});
            // cache value
            FieldProvider.FieldResolveCache[id] = field;
            return field;
        }
        FieldProvider.FieldResolveCache[id] = ResolvedField.getBlankField();
        return FieldProvider.FieldResolveCache[id];
    }

    /**
     * Verify if the ID currently being resolved has an autocompletion definition defined
     * in the field groups. If present compare the definition precursor, entity and field name.
     * The entity type (e.g. Customer) is inherited from the field group if not defined.
     * @param id {string}
     * @param path {string}
     * @param field {{}}
     * @return {null|{}}
     */
    resolveAutoCompletionSupport({id, path, field}) {
        const group = this.fieldGroups[path];
        if (group && group.autoCompleteFields) {
            const resourcePath = () => `${group.resource.charAt(0).toLowerCase()}${group.resource.slice(1)}`;
            const match = this.fieldGroups[path].autoCompleteFields
                .find(({
                    field: fieldName, precursor,
                }) => {
                    const pathway = precursor
                        || `${FieldProvider.PathDelimiter}\
${resourcePath()}\
${FieldProvider.PathDelimiter}\
${fieldName}\
${field.definition.$array ? FieldProvider.PathDelimiter : ''}`;
                    return id.endsWith(pathway);
                });
            if (match) {
                return {
                    ...match,
                    entity: match.entity || group.resource,
                };
            }
        }
        return null;
    }
}
